<template>
    <div
        class="tiny-chart"
        :class="{
            'no-sensor-data': noSensorData,
            'allow-empty-navigation': allowEmptyNavigation,
        }"
    >
        <LineChart v-if="!noSensorData" :series="series" :settings="chartSettings" />
        <div v-if="noSensorData" class="no-data">{{ $tc("noData") }}</div>
        <div v-if="busy" class="no-data">
            <Spinner class="spinner" />
        </div>
    </div>
</template>

<script lang="ts">
import _ from "lodash";
import Vue from "vue";
import { ModuleSensor, TailSensorDataRow } from "@/api";
import { DisplayStation } from "@/store/modules/stations";
import { SensorDataQuerier, SensorMeta, StandardObserver, StationQuickSensors } from "../shared/sensor_data_querier";
import { DataRow } from "@/views/viz/api";

import {
    ChartSettings,
    SeriesData,
    DataSetSeries,
    QueriedData,
    VizInfo,
    VizSensor,
    TimeRange,
    SensorDataResponse,
    ModuleSensorMeta,
} from "./vega/SpecFactory";

import LineChart from "./vega/LineChart.vue";
import Spinner from "@/views/shared/Spinner.vue";
import { getSensorStrings, parseSensorLang, SensorLang } from "./sensor-strings";

export default Vue.extend({
    name: "TinyChart",
    components: {
        Spinner,
        LineChart,
    },
    props: {
        stationId: {
            type: Number,
            required: true,
        },
        station: {
            type: DisplayStation,
            required: false,
            default: null,
        },
        querier: {
            type: SensorDataQuerier,
            required: true,
        },
        moduleKey: {
            type: String,
            required: false,
        },
        moduleHardwareId: {
            type: String,
            required: false,
        },
        sensorId: {
            type: Number,
            required: false,
            default: null,
        },
        lang: {
            type: String as () => SensorLang | null,
            default: null,
        },
        allowEmptyNavigation: {
            type: Boolean,
            default: true,
        },
    },
    data(): {
        chartSettings: ChartSettings;
        series: SeriesData[];
        vizData: { vizSensor: VizSensor; timeRange: [number, number] } | null;
        noSensorData: boolean;
        busy: boolean;
    } {
        return {
            chartSettings: ChartSettings.Tiny,
            series: [],
            vizData: null,
            noSensorData: false,
            busy: true,
        };
    },
    watch: {
        stationId(): void {
            console.log("tiny-chart:", "station-id");
            // TODO Should we also be checking if we're in view?
            void this.load();
        },
        lang(): void {
            void this.load();
        },
    },
    async mounted() {
        this.$root.$on("language-changed", this.onLanguageChanged);

        new StandardObserver().observe(this.$el, () => {
            console.log("tiny-chart:observed");
            this.series = [];
            void this.load();
        });
    },
    beforeDestroy() {
        this.$root.$off("language-changed", this.onLanguageChanged);
    },
    methods: {
        onLanguageChanged(): void {
            if (this.lang != null) {
                return;
            }

            void this.load();
        },
        async load(): Promise<void> {
            this.busy = true;
            this.noSensorData = false;
            this.resetChartState();
            const [stationData, quickSensors, meta] = await this.querier.queryTinyChartData(this.stationId);

            if (this.sensorId != null) {
                this.loadBySensorId(stationData, quickSensors, meta);
                return;
            }

            const selectedModuleKey = this.moduleKey;
            const selectedModuleHardwareId = this.moduleHardwareId;
            // eslint-disable-next-line @typescript-eslint/no-this-alias
            const thisComp = this;

            function getQuickSensor(quickSensors): VizSensor {
                let stationId, sensorModuleId, sensorId;
                if (selectedModuleKey) {
                    const module = quickSensors.station.find(
                        (s) => s.moduleKey === selectedModuleKey && (!selectedModuleHardwareId || s.moduleId === selectedModuleHardwareId)
                    );
                    if (module) {
                        stationId = module.stationId;
                        sensorModuleId = module.moduleId;
                        sensorId = module.sensorId;
                    } else {
                        thisComp.handleNoDataCase();
                    }
                } else {
                    stationId = quickSensors.station[0].stationId;
                    sensorModuleId = quickSensors.station[0].moduleId;
                    sensorId = quickSensors.station[0].sensorId;
                }
                return [stationId, [sensorModuleId, sensorId]];
            }

            function getSensorMetaAndData(
                station: DisplayStation
            ): { vizSensor: VizSensor; sensor: ModuleSensorMeta; sdr: SensorDataResponse } | null {
                // Not happy with this, no easy way until we get rid of the 'quick sensors'
                // call, which I'd love to do. Quick sensors won't return a sensor if the sensor
                // has no data and so to properly show a blank chart in that case we gotta
                // resolve the sensor to show. This uses the DisplayStation configuration data
                // to fill that in, by choosing the first one and then making up a fake vizSensor.
                if (quickSensors.station.filter((r) => r.moduleId != null).length == 0) {
                    const moduleSensor: ModuleSensor[] = _.flattenDeep(
                        station.configurations.all.map((c) => c.modules.map((m) => m.sensors))
                    );
                    if (moduleSensor.length == 0) {
                        return null;
                    }

                    const sensor = meta.findSensorByKey(moduleSensor[0].fullKey);
                    return {
                        vizSensor: [station.id, ["", 0]],
                        sensor,
                        sdr: { data: [], bucketSamples: 0, bucketSize: 0, dataEnd: null },
                    };
                }
                const vizSensor = getQuickSensor(quickSensors);
                const sensor = meta.findSensor(vizSensor);
                const [vizModuleId, vizSensorId] = vizSensor[1];
                const data = stationData.data.filter((datum) => datum.sensorId === vizSensorId && datum.moduleId === vizModuleId);

                if (data.length === 0) {
                    thisComp.handleNoDataCase();
                    return null;
                }

                const bucketSize = stationData.stations[station.id]?.bucketSize ?? 0;
                const sdr = thisComp.buildSensorDataResponse(data, bucketSize);

                return { vizSensor, sensor, sdr };
            }

            if (!this.station) {
                this.handleNoDataCase();
                return;
            }

            const maybeSensorMetaAndData = getSensorMetaAndData(this.station);
            if (!maybeSensorMetaAndData) {
                console.log("tiny-chart:empty", { quickSensors, station: this.station });
                this.handleNoDataCase();
                return;
            }
            const { vizSensor, sensor, sdr } = maybeSensorMetaAndData;

            this.applySeriesData(vizSensor, sensor, sdr, this.station.name);
        },
        loadBySensorId(
            stationData: { data: TailSensorDataRow[]; stations: Record<number, { bucketSize: number }> },
            quickSensors: StationQuickSensors,
            meta: SensorMeta
        ): void {
            try {
                const sensorId = this.sensorId;
                if (sensorId == null) {
                    this.handleNoDataCase();
                    return;
                }

                const sensor = meta.findSensorById(sensorId);
                const matchingQuickSensors = quickSensors.station.filter((row) => row.sensorId === sensorId && row.moduleId != null);
                const matchingData = stationData.data.filter((row) => row.sensorId === sensorId);
                const sensorWithData = matchingQuickSensors.find((quickSensor) =>
                    matchingData.some((row) => row.moduleId === quickSensor.moduleId)
                );
                const moduleId = sensorWithData?.moduleId || matchingQuickSensors[0]?.moduleId || matchingData[0]?.moduleId;
                const data = moduleId ? matchingData.filter((row) => row.moduleId === moduleId) : [];

                if (data.length === 0) {
                    this.handleNoDataCase();
                    return;
                }

                const vizSensor: VizSensor = [this.stationId, [data[0].moduleId, sensorId]];
                const bucketSize = stationData.stations[this.stationId]?.bucketSize ?? 0;
                const sdr = this.buildSensorDataResponse(data, bucketSize);
                this.applySeriesData(vizSensor, sensor, sdr, this.station?.name || "");
            } catch (err) {
                console.error("tiny-chart:sensor-id-error", err);
                this.handleNoDataCase();
            }
        },
        buildSensorDataResponse(data: TailSensorDataRow[], bucketSize: number): SensorDataResponse {
            return {
                data: data.map((row: TailSensorDataRow): DataRow => {
                    return {
                        time: row.time,
                        stationId: row.stationId,
                        moduleId: row.moduleId,
                        sensorId: row.sensorId,
                        value: row.min !== undefined ? row.min : null,
                        avg: row.avg,
                        min: row.min,
                        max: row.max,
                        last: row.last,
                        location: null,
                    };
                }),
                bucketSize,
                bucketSamples: 0,
                dataEnd: null,
            };
        },
        applySeriesData(vizSensor: VizSensor, sensor: ModuleSensorMeta, sdr: SensorDataResponse, stationName: string): void {
            const queried = new QueriedData("key", TimeRange.eternity, sdr);
            const strings = getSensorStrings(sensor.strings, this.lang != null ? parseSensorLang(this.lang) : null);
            const name = strings["label"] || "Unknown";
            const axisLabel = strings["axisLabel"] || strings["label"];

            const vizInfo = new VizInfo(
                sensor.key,
                [], // Scale
                { name: stationName, location: null },
                sensor.unitOfMeasure,
                sensor.key,
                name,
                sensor.viz || [],
                sensor.ranges,
                null, // Aggregation function name
                "", // ChartLabel
                axisLabel
            );

            this.series = [
                new SeriesData(
                    "key",
                    new TimeRange(queried.timeRange[0], queried.timeRange[1]),
                    new DataSetSeries(vizSensor, queried, queried),
                    queried,
                    vizInfo
                ),
            ];

            this.vizData = { vizSensor, timeRange: [queried.timeRange[0], queried.timeRange[1]] };
            this.noSensorData = false;
            this.busy = false;
        },
        resetChartState(): void {
            this.series = [];
            this.vizData = null;
        },
        handleNoDataCase() {
            this.resetChartState();
            this.noSensorData = true;
            this.busy = false;
        },
    },
});
</script>

<style scoped lang="scss">
.tiny-chart {
    margin-top: 10px;
    width: 100%;
    height: 150px;
    border: solid 1px #d8dce0;
    position: relative;
    display: flex;
    flex-direction: column;

    > .h-100 {
        flex: 1;
        min-height: 0;
        height: auto;
    }

    cursor: pointer;

    &:not(.no-sensor-data) :deep(.viz),
    &:not(.no-sensor-data) :deep(.vega-embed) {
        cursor: pointer !important;
    }

    &.no-sensor-data:not(.allow-empty-navigation) {
        cursor: default;

        .no-data {
            cursor: default;
        }
    }

    .no-data {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        height: 100%;
        width: 100%;
        background-color: #e2e4e6;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 800;
        z-index: 1;
    }
}

.tiny-chart .fit-x {
    width: 100%;
}

.tiny-chart .fit-y {
    height: 100%;
}
</style>
